import React, { useContext, useEffect, useState } from "react"
import * as styles from "./header.module.css"
import { injectIntl, FormattedMessage } from "gatsby-plugin-intl"
import MyContext from "../context/StateContext.js"

/**
 * SubscribeForm — Salesforce form-handler integration
 *
 * Submits directly to the Salesforce form-handler endpoint via a Netlify
 * proxy (see netlify.toml: /api/sf-subscribe → gladstonegallery.my.site.com
 * form-handler submit URL for form MC7DNWEWFAARAF5JLEOEM64EWPPU). The proxy
 * makes the request same-origin, so no CORS configuration is required on
 * the Salesforce side for any deploy (branch or production).
 *
 * We do NOT use Salesforce's external-form-handler-host.min.js script:
 *   1. Success is handled inline (thank-you state + GA4 "newsletter_signup"
 *      event) rather than following the endpoint's redirectUrl — this
 *      avoids the script's pop-up-blocker-prone window.open(_blank) and
 *      keeps users on the page.
 *   2. The script exposes no success/error state; this component restores
 *      the inline "Thank you" UX from the old Mailchimp implementation.
 *   3. The script's only extras (reCAPTCHA relay, Data Cloud events)
 *      aren't in use on this site.
 *
 * The payload shape matches the script's exactly:
 *   POST { formData: {...}, uniqueId: "" }
 *
 * GA4: on success we fire a "newsletter_signup" event (gtag or dataLayer,
 * whichever exists). Whoever owns the GA4 property should mark it as a
 * key event — it replaces the /newsletter/thank-you pageview conversion.
 */

const SUBMIT_URL = "/api/sf-subscribe"

// Picklist values confirmed against created Prospect records (Aug 2026).
// Checkbox `value` attrs must match the Salesforce field's accepted
// values verbatim.
const NEWSLETTER_OPTIONS = [
  "Exhibitions",
  "Art Fairs",
  "Events",
  "Editions",
  "Publications",
]

const LOCATION_OPTIONS = ["New York", "Seoul", "Brussels"]

// Honeypot — enforced client-side in handleSubmit: bots fill every field;
// humans never see this one. If it has a value we show the success state
// and submit nothing. (If SF team adds a server-side honeypot field later,
// rename this to match and stop deleting it from the payload.)
const HONEYPOT_NAME = "gg_extra_field"

const SubscribeForm = ({ intl }) => {
  const { updateSubscribeOpen } = useContext(MyContext)
  const [status, setStatus] = useState("idle") // idle | submitting | success | error

  // Capture UTMs on first load; Gatsby SPA navigation drops query params,
  // so persist them for the session. Populated into hidden fields below.
  // The utm_source / utm_medium / utm_campaign fields exist on the
  // Salesforce form definition (confirmed on Prospect records).
  const [utms, setUtms] = useState({})
  useEffect(() => {
    const stored = sessionStorage.getItem("gg-utms")
    if (stored) {
      setUtms(JSON.parse(stored))
      return
    }
    const params = new URLSearchParams(window.location.search)
    const captured = {}
    for (const key of ["utm_source", "utm_medium", "utm_campaign"]) {
      const v = params.get(key)
      if (v) captured[key] = v
    }
    if (Object.keys(captured).length) {
      sessionStorage.setItem("gg-utms", JSON.stringify(captured))
      setUtms(captured)
    }
  }, [])

  const handleSubmit = async e => {
    e.preventDefault()
    if (status === "submitting") return
    setStatus("submitting")

    const formData = new FormData(e.target)
    const dataObject = {}
    for (const [key, value] of formData.entries()) {
      if (Object.prototype.hasOwnProperty.call(dataObject, key)) {
        if (!Array.isArray(dataObject[key])) dataObject[key] = [dataObject[key]]
        dataObject[key].push(value)
      } else {
        dataObject[key] = value
      }
    }

    // Honeypot: if a bot filled the hidden field, pretend success and
    // submit nothing. Otherwise strip the empty field from the payload.
    if (dataObject[HONEYPOT_NAME]) {
      setStatus("success")
      setTimeout(() => updateSubscribeOpen(false), 5000)
      return
    }
    delete dataObject[HONEYPOT_NAME]

    try {
      const response = await fetch(SUBMIT_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ formData: dataObject, uniqueId: "" }),
      })
      if (!response.ok) throw new Error(`HTTP ${response.status}`)

      // Inline thank-you; deliberately not following the endpoint's
      // redirectUrl. Fire the GA4 conversion the thank-you page would
      // have provided:
      if (typeof window.gtag === "function") {
        window.gtag("event", "newsletter_signup", { method: "popup" })
      } else if (window.dataLayer) {
        window.dataLayer.push({ event: "newsletter_signup", method: "popup" })
      }

      setStatus("success")
      setTimeout(() => updateSubscribeOpen(false), 5000)
    } catch (err) {
      console.error("Subscribe submit failed:", err)
      setStatus("error")
    }
  }

  if (status === "success") {
    return (
      <div>
        <h2 className={styles.popUpHeadline}>
          <FormattedMessage id="thank_you" />
        </h2>
        <p>Gallery news and updates will arrive soon in your inbox.</p>
        <button
          onClick={() => updateSubscribeOpen(false)}
          className={styles.submit}
          type="button"
        >
          Close
        </button>
      </div>
    )
  }

  return (
    <div>
      <h2 className={styles.popUpHeadline}>Stay In Touch</h2>
      <p>
        Sign up to be notified about upcoming exhibitions, art works, events,
        and more.{" "}
      </p>
      <form onSubmit={handleSubmit}>
        <div className={styles.nameRow}>
          <input
            type="text"
            name="firstname"
            autoCapitalize="off"
            placeholder={intl.formatMessage({ id: "first" })}
            required
            className={styles.emailInput}
          />
          <input
            type="text"
            name="lastname"
            autoCapitalize="off"
            placeholder={intl.formatMessage({ id: "last" })}
            required
            className={styles.emailInput}
          />
        </div>
        <input
          type="email"
          name="email"
          autoCapitalize="off"
          placeholder={intl.formatMessage({ id: "email" })}
          required
          className={styles.emailInput}
        />

        <div className={styles.checkColumns}>
          <div className={styles.checkGroup}>
            <p className={styles.fieldLabel}>Interests</p>
            {NEWSLETTER_OPTIONS.map(option => (
              <label className={styles.check} key={option}>
                <input
                  type="checkbox"
                  name="additionalnewsletters"
                  value={option}
                  defaultChecked
                />
                {option}
                <span className={styles.checkmark}></span>
              </label>
            ))}
          </div>
          <div className={styles.checkGroup}>
            <p className={styles.fieldLabel}>Locations</p>
            {LOCATION_OPTIONS.map(option => (
              <label className={styles.check} key={option}>
                <input
                  type="checkbox"
                  name="gladstonelocations"
                  value={option}
                  defaultChecked
                />
                {option}
                <span className={styles.checkmark}></span>
              </label>
            ))}
          </div>
        </div>

        {/* UTM attribution — hidden, populated from session capture */}
        {Object.entries(utms).map(([key, value]) => (
          <input type="hidden" name={key} value={value} readOnly key={key} />
        ))}

        {/* Honeypot — visually hidden, tab-skipped, ignored by humans.
            Do NOT use display:none — some bots skip hidden fields. */}
        <input
          type="text"
          name={HONEYPOT_NAME}
          tabIndex={-1}
          autoComplete="one-time-code"
          aria-hidden="true"
          style={{
            position: "absolute",
            left: "-9999px",
            height: 0,
            width: 0,
            opacity: 0,
          }}
        />

        <button
          type="submit"
          className={styles.submit}
          disabled={status === "submitting"}
        >
          {status === "submitting"
            ? "…"
            : intl.formatMessage({ id: "subscribe" })}
        </button>

        <div className={styles.extraPadding}></div>
        {status === "error" && (
          <div className={styles.errorMessage}>
            Something went wrong. Please try again.
          </div>
        )}
      </form>
    </div>
  )
}

export default injectIntl(SubscribeForm)
